import { currencyFloatToInteger, integerToCurrencyFloat } from '@acme/b2b/pricing';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { PaymentProcessError } from '@b2b/common/directives/payment/types';
import { PromptSaveDirectiveModule } from '@b2b/common/directives/promptSave/promptSave.directive';
import { Notice, NoticeFactory } from '@b2b/common/factories/notice';
import { ThemesData } from '@b2b/common/resolvers';
import { safeApply } from '@b2b/common/rxjs';
import { AddressService, AddressServiceModule } from '@b2b/common/services/address';
import { LocationService, LocationServiceModule } from '@b2b/common/services/address/location.service';
import { ApiService } from '@b2b/common/services/api/types';
import { LunchLitelyService, LunchLitelyServiceModule } from '@b2b/common/services/lunchLitely/lunchLitely.service';
import {
  CheckoutProcessService,
  CheckoutProcessServiceModule,
} from '@b2b/common/services/checkout/checkoutProcess.service';
import { OrgOrderCreateData, SecureOrgOrderProps } from '@b2b/common/services/checkout/org-checkout.types';
import { isACMECORSCheckoutError } from '@b2b/common/services/checkout/types';
import { FeatureService, FeatureServiceModule } from '@b2b/common/services/feature/feature.service';
import { PaymentService, PaymentServiceModule } from '@b2b/common/services/payment/payment.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, TODO, UIBModalService } from '@b2b/types/core';
import { EventVo } from '@b2b/types/event';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, ISCEService, IScope, ITimeoutService } from 'angular';
import {
  createPrivateEventModalConfig,
  CreatePrivateEventModalControllerModule,
} from '../../../programs/modals/createPrivateEvent/controllers/CreatePrivateEventController';
import {
  OrdersCheckoutAddEventModalControllerModule,
  OrdersCheckoutAddEventModalData,
} from './OrdersCheckoutAddEventModalController';
import { getMembershipIdFromOrder } from '../../utils';

type ReservationErrorResponse<T> = { success: false; status: string; code: number; error: string; errorData: T };

type ReservationNotFoundError = ReservationErrorResponse<{
  timestamp: string;
  message: string;
  errorCode: 1057;
  type: 'RESERVATION_DNE';
  requestId: string;
  path: string;
}>;

type ReservationAvailabilityError = ReservationErrorResponse<{
  message: string;
  available: number;
  requested: string;
  eventId: string;
}>;

function isReservationNotFoundError<T>(
  error: ReservationNotFoundError | ReservationErrorResponse<T>,
): error is ReservationNotFoundError {
  return (error as ReservationNotFoundError)?.errorData?.type === 'RESERVATION_DNE';
}

export const OrdersCheckoutControllerModule = angular
  .module('acme.view.application.orders.checkout.controllers', [
    uiRouter,
    'ui.bootstrap',
    'angularUUID2',
    'acme.common.service.api',
    'acme.common.service.calendar',
    'acme.common.service.validate',
    'acme.common.service.order',
    'acme.common.service.helpers',
    'acme.common.service.permissions',
    'acme.common.service.paymentProcessor',
    'acme.common.service.themesHelper',
    FeatureServiceModule,
    'acme.common.factory.notice',
    'acme.common.filter.saleChannel',
    'acme.common.service.userPreferences',
    OrdersCheckoutAddEventModalControllerModule,
    'acme.view.application.reports.create.showMessageModal.controllers',
    PromptSaveDirectiveModule,
    CheckoutProcessServiceModule,
    LocationServiceModule,
    PaymentServiceModule,
    AddressServiceModule,
    CreatePrivateEventModalControllerModule,
    NoticesComponentModule,
    LunchLitelyServiceModule,
  ])

  .controller('OrdersCheckoutController', [
    '$scope',
    '$sce',
    '$state',
    '$filter',
    '$timeout',
    '$uibModal',
    'uuid2',
    'EventData',
    'ApiService',
    'ValidateService',
    'OrderService',
    'HelpersService',
    'CalendarService',
    'PermissionsService',
    'ThemesHelperService',
    'NoticeFactory',
    'ThemesData',
    'UserPreferencesService',
    'FeatureService',
    'CheckoutProcessService',
    'LocationService',
    'PaymentService',
    'AddressService',
    'LunchLitelyService',
    function (
      $scope: IScope & { ordersCheckout: TODO; noticeTimer: TODO },
      $sce: ISCEService,
      $state: StateService,
      $filter: IFilterService,
      $timeout: ITimeoutService,
      $uibModal: UIBModalService,
      uuid2: TODO,
      EventData: TODO,
      ApiService: ApiService,
      ValidateService: TODO,
      OrderService: TODO,
      HelpersService: TODO,
      CalendarService: TODO,
      PermissionsService: PermissionsService,
      ThemesHelperService: ThemesHelperService,
      NoticeFactory: NoticeFactory,
      ThemesData: { data: ACMEResponse<ThemesData> },
      UserPreferencesService: TODO,
      FeatureService: FeatureService,
      CheckoutProcessService: CheckoutProcessService,
      LocationService: LocationService,
      paymentService: PaymentService,
      addressService: AddressService,
      lunchLitelyService: LunchLitelyService,
    ) {
      const ctrl = {
        requiresCardData: true,

        model: {
          accountId: '',
          customerId: '',
          events: [],
          notes: '',
          eventId: '',
          contactEmail: '',
          phoneNumber: '',
          contactLastName: '',
          contactFirstName: '',
          address: {},
        } as TODO,

        themes: {},

        payment: {} as TODO,
        partnerPrimaryContact: false as TODO | false,
        customerContact: false as TODO | false,
        emailOption: null as TODO | null,
        validateEmail: true,
        hasEmailError: false,

        invalidQuantity: false,
        isSubmitted: false,
        isPaymentProcessing: false,
        isAccount: false,

        requestUUID: false as string | false,

        noticeTimer: '',
        addOnQuantity: 0,
        quantity: 0,
        total: 0,
        subTotal: 0,
        discountTotal: 0,
        tax: 0,

        addModal: {},

        notice: new NoticeFactory() as Notice,
        dateFormat: UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
        hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
        events: [] as TODO[],
        event: {} as TODO,
        errors: {},
        order: false as TODO | false,
        accountContacts: [] as TODO[],
        currentAccountContact: false as TODO | false,
        selectedAccountContact: false as TODO | false,
        reservation: null as TODO | null,

        invoiceAfterEvent: false,

        enableCSRToggle: false,

        enableSalesTax: false,
        taxExemptEnabled: false,

        isEnabledCORSCheckout: false,
        corsCheckoutCreditCardMessage: `Payment will be securely collected when you select "Take Payment & Book Order"`,

        viewIds: ['application.orders.checkout', 'application.orders.edit', 'application.orders.detail.summary'],

        saveMessage: 'Do you want to leave this page? Doing so will cancel this order.',

        /**
         * WARNING!
         *
         * Do not re-enable buttons when successful
         * This can cause duplicate orders
         *
         * DO NOT SET the following variables:
         *
         * $scope.ordersCheckout.isSubmitted
         * $scope.ordersCheckout.isPaymentProcessing
         */
        completeSuccess: (params: { orderId: number }) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          ctrl.resetModel();
          if ($state.params.accountId) {
            $state.go('application.orders.detail.summary', {
              accountId: $state.params.accountId,
              orderId: params.orderId,
            });
          } else {
            $state.go('application.orders.detail.summary', { orderId: params.orderId });
          }

          /**
           * State should have changed it is safe to restore on next tick
           *   requestUUID: false
           */
          // next tick
          $timeout(() => {
            ctrl.requestUUID = false;
          }, 0);
        },

        completeError: (response: { status: number; data: PaymentProcessError }) => {
          /**
           * When error status is 409 it is a duplicate order
           * We must call to retrieve the actual order based on the UUID
           */
          if (response.status === 409 && response.data.errorCode === 20152) {
            ctrl.getDuplicateOrder();
          } else {
            ctrl.isSubmitted = false;
            ctrl.isPaymentProcessing = false;

            $scope.$broadcast('Payment::Process::Error', { response: response });

            const messages = response.data.errors
              ? response.data.errors.map((error) => ({ message: error.message.replace(/^.*?:/, '') }))
              : [{ message: response.data.error.replace(/^.*?:/, '') }];

            ctrl.notice.showErrorMessages(messages);

            /**
             * This is order duplication prevention
             * We create the checkout UUID and tack it onto the checkout request
             * Only rewrite the requestUUID when there are no errors and a checkout has taken place
             * This will always be refreshed when the page is refreshed, but, in the context of an order
             * we need to re-issue another uuid if a normal error happens
             */
            ctrl.requestUUID = uuid2.newuuid();
          }
        },

        init: function () {
          paymentService.processData$
            .pipe(
              safeApply($scope, {
                next: ({ chargeAmount, paymentMethod }) => {
                  ctrl.requiresCardData = paymentMethod === 'CreditCard' && chargeAmount > 0;
                },
              }),
            )
            .subscribe();

          if (PermissionsService.check('CSR')) {
            ctrl.enableCSRToggle = true;
          }

          lunchLitelyService.isEnabled('tax_exempt_orgs').then((enabled) => {
            ctrl.taxExemptEnabled = enabled;
            ctrl.events.forEach((event) => ctrl.calculateTotal(event));
          });

          if ($state.params.customerId) {
            // Set customer id in model and fetch after getting the partner
            ctrl.model.customerId = $state.params.customerId;
          }

          if ($state.params.accountId && PermissionsService.check('ACCT_ORDERS_CREATE')) {
            ctrl.model.accountId = $state.params.accountId;
            ctrl.isAccount = true;
            /**
             * No promises used but there is a callback chain here
             * getPartner -> getCustomer -> getPartnerContacts
             */
            ctrl.getPartner(ctrl.model.accountId);
          } else if (PermissionsService.check('ORDER_CREATE')) {
            ctrl.emailOption = '1';
            ctrl.model.accountId = null;
          } else {
            $state.go('home.default', {}, { notify: true });
          }

          $scope.$emit('Application::contentTitleChange', {
            title: 'Create Order',
            buttonLeft: {
              label: 'Cancel',
              action: () => {
                ctrl.cancel();
              },
            },
            buttonRight: {
              label: 'Book Order',
              action: () => {
                ctrl.processPayment();
              },
            },
          });

          if (EventData && EventData.data.success) {
            ctrl.events.push(ctrl.process(EventData.data.payload, false));
            ctrl.model.eventId = ctrl.event.id;
            ctrl.model.startTime = EventData.data.payload.startTime;
          } else {
            // noop
          }

          if (ctrl.model.accountId) {
            ctrl.getPayments(ctrl.model.accountId);
          }

          ThemesHelperService.resetModel();

          if (ThemesData && ThemesData.data.success) {
            ctrl.initThemes(ThemesData);
          }

          // Setup Reservation
          ApiService.reservations.create([]).then((response) => {
            ctrl.reservation = response.payload;

            // Call Reservation update in case we have pre-populated events
            ctrl.updateReservation();
          });

          $scope.$on('Payment::Complete', ctrl.onPaymentComplete);
          $scope.$on('Payment::Error', ctrl.onPaymentError);
          // TODO: Not entirely undesired but needs work
          // $scope.$on('$destroy', $scope.$watch('ordersCheckout.selectedAccountContact', ctrl.onSelectedAccountUpdate));
          $scope.$on('$destroy', $scope.$watch('ordersCheckout.invoiceAfterEvent', ctrl.onCheckedInvoiceAfterEvent));

          // Clear reservation
          $scope.$on('$destroy', ctrl.cancelReservation.bind(this));
        },

        initThemes: function (response) {
          ThemesHelperService.setModel(response.data.payload);
          CheckoutProcessService.initACMECheckout();

          ctrl.themes = {
            policyTerms: $sce.trustAsHtml(ThemesHelperService.getHTMLVariable('checkout-process-page-terms')),
          };

          ctrl.enableSalesTax = ThemesHelperService.getFeatureFlag('enableSalesTax');
          ctrl.isEnabledCORSCheckout = CheckoutProcessService.isEnabledCORSCheckout();
        },

        resetModel: function () {
          ctrl.reservation = null;
          ctrl.model.contactFirstName = '';
          ctrl.model.contactLastName = '';
          ctrl.model.contactEmail = '';
          ctrl.model.phoneNumber = '';
          ctrl.model.address = {};
          ctrl.model.notes = '';
          ctrl.model.events = [];

          ctrl.addOnQuantity = 0;
          ctrl.quantity = 0;
          ctrl.total = 0;

          ctrl.clearOrder();
          OrderService.clear();
        },

        clearOrder: function () {
          for (let j = 0, jj = ctrl.events.length; j < jj; j++) {
            for (let i = 0, ii = ctrl.events[j].priceList.prices.length; i < ii; i++) {
              ctrl.events[j].priceList.prices[i].quantity = 0;
            }

            if (ctrl.events[j].hasOwnProperty('addOns') && ctrl.events[j].addOns.inventoryAddOns instanceof Array) {
              for (let i = 0, ii = ctrl.events[j].addOns.inventoryAddOns.length; i < ii; i++) {
                ctrl.events[j].addOns.inventoryAddOns[i].totalQuantity = 0;
              }
            }
          }
        },

        restoreOrder: function () {
          if (ctrl.order.hasOwnProperty('orderRequest') && ctrl.order.orderRequest) {
            ctrl.processOrderEvents(ctrl.order.orderRequest.orderRequestItems || [], true);
          } else if (ctrl.order.hasOwnProperty('cart')) {
            ctrl.processOrderEvents(ctrl.order.cart.events || [], true);
          } else {
            if (ctrl.hasPriceList(ctrl.events[0])) {
              for (let i = 0, ii = ctrl.events[0].priceList.prices.length; i < ii; i++) {
                for (let j = 0, jj = ctrl.order.priceList.prices.length; j < jj; j++) {
                  if (
                    ctrl.events[0].priceList.prices[i].personType.id === ctrl.order.priceList.prices[j].personType.id
                  ) {
                    console.log(
                      'OrdersCheckoutController::restoreOrder',
                      ctrl.events[0].priceList.prices[i].quantity,
                      ctrl.order.priceList.prices[j].quantity,
                    );
                    ctrl.events[0].priceList.prices[i].quantity = ctrl.order.priceList.prices[j].quantity;
                    break;
                  }
                }
              }
            }

            if (ctrl.events[0].hasOwnProperty('addOns') && ctrl.events[0].addOns.inventoryAddOns instanceof Array) {
              for (let i = 0, ii = ctrl.events[0].addOns.inventoryAddOns.length; i < ii; i++) {
                if (ctrl.order.hasOwnProperty('addOns') && ctrl.order.addOns.inventoryAddOns instanceof Array) {
                  for (let j = 0, jj = ctrl.order.addOns.inventoryAddOns.length; j < jj; j++) {
                    if (ctrl.events[0].addOns.inventoryAddOns[i].id === ctrl.order.addOns.inventoryAddOns[j].id) {
                      console.log(
                        'OrdersCheckoutController::restoreOrder',
                        ctrl.events[0].addOns.inventoryAddOns[i].quantity,
                        ctrl.order.addOns.inventoryAddOns[j].quantity,
                      );
                      ctrl.events[0].addOns.inventoryAddOns[i].totalQuantity =
                        ctrl.order.addOns.inventoryAddOns[j].totalQuantity;
                      ctrl.events[0].addOns.inventoryAddOns[i].discountedPrice =
                        ctrl.order.addOns.inventoryAddOns[j].discountedPrice ||
                        ctrl.order.addOns.inventoryAddOns[j].price;
                      break;
                    }
                  }
                } else {
                  ctrl.events[0].addOns.inventoryAddOns[i].totalQuantity = 0;
                  ctrl.events[0].addOns.inventoryAddOns[i].discountedPrice = ctrl.order.addOns.inventoryAddOns[0].price;
                }
              }
            }

            ctrl.calculateTotal(ctrl.events[0]);
          }
        },

        checkCart: function () {
          return $state.params.accountId && ctrl.order && ctrl.order.cart;
        },

        proceed: function () {
          OrderService.clear();
          $scope.$broadcast('promptSave::Complete');
        },

        addNewContact: function () {
          const addModal = $uibModal.open<TODO>({
            templateUrl: 'views/application/views/partners/create/modals/ManageSecondaryContactModal.tpl.html',
            size: 'lg',
            controller: 'ManageSecondaryContactModalController',
            resolve: {
              ValidateService: () => ValidateService,
              alertMessage: () => null,
              organizationId: () => ctrl.model.accountId,
              organizationName: () => ctrl.model.accountName,
              contact: () => null,
              address: () => null,
            },
          });

          addModal.result
            .then((contact) => {
              let props: TODO = {};
              //add the secondaryContact
              if (contact) {
                props = contact;
                props.organizationId = ctrl.model.accountId;
                ctrl.isAccount = true;

                ApiService.organizations.contacts
                  .create(props)
                  .then((response) => {
                    const _contact = response.data.payload;

                    if (!ctrl.currentAccountContact) {
                      ctrl.currentAccountContact = {};
                    }

                    ctrl.currentAccountContact.id = _contact.id;
                    ctrl.currentAccountContact.contactFirstName = _contact.firstName;
                    ctrl.currentAccountContact.contactLastName = _contact.lastName;
                    ctrl.currentAccountContact.phoneNumber = _contact.phoneNumber;
                    ctrl.currentAccountContact.email = _contact.email;

                    $scope.$broadcast('ContactsTypeahead::Refresh', { organizationId: ctrl.model.accountId });
                  })
                  .catch(() => {
                    ctrl.showMessage({
                      title: 'Unable to create contact',
                      message: 'Failed to create contact for organization.',
                    });
                  });
              }
            })
            .catch(() => {
              // noop
            });
        },

        showMessage: function (props: { title: string; message: string; okText?: string }) {
          if (!props.message) {
            return Promise.resolve(false);
          }

          return $uibModal.open<TODO>({
            templateUrl: 'views/application/views/reports/create/modals/showMessage.tpl.html',
            size: 'md',
            controller: 'ReportsCreateShowMessageModalController',
            resolve: {
              currentTitle: () => props.title,
              currentMessage: () => props.message,
              safe: () => true,
            },
          }).result;
        },

        updateQuantity: function (price, quantity, event) {
          price.quantity = quantity;
          ctrl.calculateTotal(event);
        },

        getReservationItemsFromEvents: function () {
          return ctrl.events
            .filter((event) => event.quantity > 0)
            .map((event) => {
              return {
                eventId: event.id,
                quantity: event.quantity,
              };
            });
        },

        updateReservation: function () {
          if (!ctrl.reservation) {
            return;
          }

          const latestReservationItems = ctrl.getReservationItemsFromEvents();
          const hasChanged =
            JSON.stringify(latestReservationItems) !== JSON.stringify(ctrl.reservation.reservationItems);

          if (hasChanged) {
            const latestReservation = {
              id: ctrl.reservation.id,
              reservationItems: latestReservationItems,
            };

            ApiService.reservations
              .update(latestReservation)
              .then((response) => ctrl.onReservationSuccess(response))
              .catch((response) => ctrl.onReservationError(response));
          }
        },

        cancelReservation: function () {
          // If this is null, then we've either not initialised or we've completed the transaction
          if (ctrl.reservation && ctrl.reservation.id) {
            ApiService.reservations.cancel(ctrl.reservation.id);
          }
        },

        hasReservationErrors: function () {
          return ctrl.events.some((event) => {
            return event._hasReservationError === true;
          });
        },

        resetEventReservationErrors: function () {
          ctrl.events.forEach((event) => {
            event._hasReservationError = false;
          });
        },

        onReservationSuccess: function (updatedReservation) {
          // Consider extra meta-data in reservation items from response
          // i.e. id, and reservationId - need to send? need to keep?

          const updatedReservationItems = updatedReservation.payload.reservationItems.map((event) => {
            return {
              quantity: event.quantity,
              eventId: event.eventId,
            };
          });

          // Update local reservation on success
          Object.assign(ctrl.reservation, {
            expirationTimestamp: updatedReservation.payload.expirationTimestamp, // Keep for future use
            reservationItems: updatedReservationItems,
          });

          ctrl.notice.hideErrorMessages();
          ctrl.resetEventReservationErrors();
        },

        setReservationError: function (eventId: string) {
          const event = ctrl.events.filter((event) => {
            return event.id === eventId;
          });

          if (event[0]) {
            event[0]._hasReservationError = true;
          }
        },

        getReservationErrorMessage: function (errorData: ReservationAvailabilityError['errorData']) {
          const noneAvailable = errorData.available === 0;
          const errorEvents = ctrl.events.filter((event) => {
            return event.id === errorData.eventId;
          });
          const currentReservationForErroringEvent = ctrl.reservation.reservationItems.filter((event) => {
            return event.eventId === errorData.eventId;
          });
          const numberTicketsRequested = parseInt(errorData.requested, 10);
          let numberTicketsReserved = 0;

          // Get number of tickets currently reserved, if any
          if (currentReservationForErroringEvent[0]) {
            numberTicketsReserved = parseInt(currentReservationForErroringEvent[0].quantity, 10);
          }

          let message = '';
          if (noneAvailable && numberTicketsReserved === 0) {
            message += 'There are no more';
          } else {
            message += `Only ${errorData.available + numberTicketsReserved}`;
          }

          // Setup error message for user
          const requested = numberTicketsRequested + numberTicketsReserved;
          message += ` ticket(s) available for '${errorEvents[0].name}' (you requested ${requested})`;

          return message;
        },

        onReservationError: function (response: ReservationNotFoundError | ReservationAvailabilityError) {
          if (isReservationNotFoundError(response)) {
            ctrl.notice.showErrorMessages([{ message: response.errorData.message }]);
            return;
          }

          const message = ctrl.getReservationErrorMessage(response.errorData);

          if (response.errorData && response.errorData.eventId) {
            ctrl.setReservationError(response.errorData.eventId);
          }

          ctrl.notice.showErrorMessages([{ message }]);
        },

        addPrivateEvent: function () {
          $uibModal
            .open<TODO>(
              createPrivateEventModalConfig(() => {
                return {
                  startTime: ctrl.model.startTime,
                  accountId: ctrl.model.accountId,
                  accountCategoryRelation: ctrl.model.accountCategoryRelation,
                };
              }),
            )
            .result.then((result) => {
              ctrl.notice.hideMessages();
              ctrl.notice.hideSuccessMessages();
              ctrl.notice.hideErrorMessages();
              ctrl.isSubmitted = false;

              ctrl.events.push(ctrl.process(result.data.payload[0] as EventVo, false));
            });
        },

        addEvent: function () {
          $uibModal
            .open<TODO[]>({
              templateUrl: 'views/application/views/orders/checkout/modals/addEvent.tpl.html',
              size: 'lg',
              controller: 'OrdersCheckoutAddEventModalController',
              resolve: {
                MODAL_DATA: () => {
                  return {
                    accountId: $state.params.accountId ? $state.params.accountId : null,
                    startTime: ctrl.model.startTime,
                    membershipId: ctrl.getMembershipIdFromOrder(ctrl.model),
                    eventIds: []
                  } satisfies OrdersCheckoutAddEventModalData;
                },
              },
            })
            .result.then((events) => {
              ctrl.notice.hideSuccessMessages();
              ctrl.notice.hideErrorMessages();
              ctrl.isSubmitted = false;

              ctrl.events = ctrl.events.concat(events.map((event) => ctrl.process(event, false)));
            })
            .catch(() => {
              // noop
            });
        },

        removeEvent: function (index) {
          ctrl.events.splice(index, 1);
          ctrl.calculateSubTotal();
        },

        hasCustomerId: function () {
          return ctrl.model.customerId && ctrl.model.customerId.length;
        },

        process: function (event: TODO, checkAddons: boolean) {
          event.startDate = CalendarService.toFormat(CalendarService.parseDate(event.startTime), ctrl.dateFormat);
          event.startHour = CalendarService.toFormat(CalendarService.parseDate(event.startTime), ctrl.hourFormat);

          event.quantity = 0;
          event.total = 0;

          if (!ctrl.enableCSRToggle) {
            event.toggleCSRPrices = true;
            event.toggleCSRAddons = true;
          }

          if (event.priceList && event.priceList.prices instanceof Array) {
            for (let i = 0, ii = event.priceList.prices.length; i < ii; i++) {
              if (!ctrl.model.accountId && !event.priceList.prices[i].discountedPrice) {
                event.priceList.prices[i].discountedPrice = event.priceList.prices[i].price;
              }
            }
          } else {
            ctrl.notice.showErrorMessages([
              {
                message:
                  'No price list found. ' +
                  (ctrl.isAccount ? 'Please make sure your price list has the reseller sale channel enabled.' : ''),
              },
            ]);
          }

          // TODO: Currently no support for discounted add-ons so patching
          if (event.hasOwnProperty('addOns') && event.addOns.inventoryAddOns instanceof Array) {
            for (let i = 0, ii = event.addOns.inventoryAddOns.length; i < ii; i++) {
              event.addOns.inventoryAddOns[i].discountedPrice = event.addOns.inventoryAddOns[i].price;
              if (!checkAddons) {
                event.addOns.inventoryAddOns[i].totalQuantity = 0;
              } else {
                event.addOns.inventoryAddOns[i].totalQuantity = event.addOns.inventoryAddOns[i].quantity || 0;
              }
            }
          }

          return event;
        },

        processOrderEvents: function (events, eventHasAddons) {
          for (let i = 0, ii = events.length; i < ii; i++) {
            const event = events[i];
            event.startTime = event.eventStartDate || event.startTime;
            ctrl.events.push(ctrl.process(event, eventHasAddons));
            ctrl.calculateTotal(ctrl.events[i]);
          }
        },

        processContacts: function (list) {
          const contacts: TODO[] = [];
          let contact: TODO;

          for (let i = 0, ii = list.length; i < ii; i++) {
            contact = list[i];
            contact.displayName = contact.firstName + ' ' + contact.lastName;

            if (ctrl.hasCustomerId() && ctrl.customerContact) {
              /**
               *  Set the `Order Contact` to current customer if we know they are the same
               *  Otherwise choose the primary contact
               *  If no contact is found we need to set the selectedAccountContact
               *  contact to the primary contact but not change the `Payment Information` fields
               */
              if (ctrl.customerContact.customerId === contact.externalId) {
                ctrl.selectedAccountContact = contact;
              }
            } else if (contact.primary) {
              ctrl.selectedAccountContact = contact;
            }

            if (contact.primary) {
              contact.displayName = contact.displayName + ' (Primary)';
            }
            contacts.push(contact);
          }
          return contacts;
        },

        getPartner: function (accountId: number) {
          if (accountId) {
            ApiService.partners
              .detail(accountId)
              .then((response) => {
                const partner = response.data.payload;

                ctrl.model.accountCategoryRelation = partner.accountCategoryRelation;

                // Used in the event that getCustomer call fails
                ctrl.partnerPrimaryContact = {
                  firstName: partner.primaryContact.firstName,
                  lastName: partner.primaryContact.lastName,
                  phoneNumber: partner.primaryContact.phoneNumber,
                  email: partner.primaryContact.email,
                };

                if (
                  ctrl.model.accountCategoryRelation &&
                  partner.requests instanceof Array &&
                  partner.requests.length
                ) {
                  ctrl.model.accountCategoryRelation.notes = partner.requests[0].posNotes;
                }

                ctrl.selectEmailOption();

                if (ctrl.hasCustomerId()) {
                  ctrl.getCustomer(ctrl.model.customerId);
                } else {
                  ctrl.getPartnerContacts(ctrl.model.accountId);
                }
              })
              .catch((response: TODO) => {
                ctrl.notice.showErrorMessages([{ message: response.data.error }]);
              });
          }
        },

        getCustomer: function (customerId: number) {
          if (customerId) {
            ApiService.customers
              .detail({ customerId: customerId })
              .then((response) => {
                const customer = response.data.payload;

                ctrl.customerContact = customer;
                ctrl.model.contactFirstName = customer.firstName;
                ctrl.model.contactLastName = customer.lastName;
                ctrl.model.phoneNumber = customer.phoneNumber;
                ctrl.model.contactEmail = customer.email;

                ctrl.getPartnerContacts(ctrl.model.accountId);
              })
              .catch(() => {
                // Customer call has failed assign primary contact data instead
                ctrl.model.contactFirstName = ctrl.partnerPrimaryContact.firstName;
                ctrl.model.contactLastName = ctrl.partnerPrimaryContact.lastName;
                ctrl.model.phoneNumber = ctrl.partnerPrimaryContact.phoneNumber;
                ctrl.model.contactEmail = ctrl.partnerPrimaryContact.email;

                // Not showing an error here if partner fails this will not fire
              });
          }
        },

        getPartnerContacts: function (accountId) {
          ApiService.partners.contacts
            .list(accountId, HelpersService.getPaginator())
            .then((response) => {
              ctrl.accountContacts = ctrl.processContacts(response.data.payload.list);
            })
            .catch(() => {
              //TODO: don't show error as this should not be blocking orders. Just don't show the contacts dropdown?
            });
        },

        getPayments: function (accountId) {
          ApiService.partners.payments
            .account(accountId)
            .then((response) => {
              ctrl.payment = response.data.payload;
              ctrl.payment.ticketDelivery = 'willCall';

              ctrl.order = OrderService.fetch();
              if (ctrl.order) {
                if (ctrl.enableCSRToggle && ctrl.hasPriceList(ctrl.events[0])) {
                  ctrl.events[0].toggleCSRPrices = ctrl.order.toggleCSRPrices;
                  ctrl.events[0].toggleCSRAddons = ctrl.order.toggleCSRAddons;
                }
                //next tick - need this or init for quantity gets called after model setting quantity to 0
                $timeout(() => {
                  ctrl.restoreOrder();
                }, 0);
              }

              //next tick
              $timeout(() => {
                ctrl.calculateSubTotal();
              }, 0);
            })
            .catch(() => {
              ctrl.order = OrderService.fetch();

              if (ctrl.order) {
                ctrl.restoreOrder();
              }

              //next tick
              $timeout(() => {
                ctrl.calculateSubTotal();
              }, 0);

              ctrl.calculateSubTotal();
            });
        },

        getDuplicateOrder: function () {
          const uuid = ctrl.requestUUID ? ctrl.requestUUID : '';

          ApiService.system
            .uuidObjectRegistry({ uuid })
            .then((response) => {
              // Navigate to duplicate order
              $state.go('application.orders.detail.summary', { orderId: response.data.payload.orderId });
            })
            .catch((response) => {
              ctrl.completeError(response);
            });
        },

        isValidQuantity: function (event) {
          let r = true;

          if (ctrl.isSubmitted) {
            if (!ctrl.order.orderRequest) {
              r = event.quantity <= event.capacity - event.soldQuantity;
            } else {
              r = event.quantity > 0;
            }
          }

          return r;
        },

        hasPriceList: function (event) {
          return event && event.priceList && event.priceList.prices instanceof Array;
        },

        showsBasePricing: function (item) {
          return ctrl.enableSalesTax && item && item.basePrice != null;
        },

        useTaxExemptPricing: function (item) {
          return (
            ctrl.enableSalesTax &&
            ctrl.taxExemptEnabled &&
            ctrl.payment &&
            ctrl.payment.taxExempt &&
            item &&
            item.basePrice != null
          );
        },

        getDisplayPrice: function (item) {
          return ctrl.showsBasePricing(item) ? item.basePrice : item.price;
        },

        getDisplayDiscountedPrice: function (item) {
          if (ctrl.showsBasePricing(item)) {
            return item.discountedBasePrice ?? item.basePrice;
          }
          return item.discountedPrice;
        },

        getPayablePrice: function (item) {
          if (ctrl.useTaxExemptPricing(item)) {
            return item.discountedBasePrice ?? item.basePrice;
          }
          return item.discountedPrice;
        },

        calculateTotal: function (event) {
          let total = 0;
          let baseTotal = 0;
          let discountedBaseTotal = 0;
          let quantity = 0;
          let addOnQuantity = 0;
          let currentPrice = 0;

          const accumulateBase = (item, itemQuantity) => {
            if (!ctrl.enableSalesTax) {
              return;
            }
            const basePriceValue = item.basePrice ?? item.price;
            const discountedBasePriceValue = item.discountedBasePrice ?? item.basePrice ?? item.discountedPrice;
            const basePriceInt = currencyFloatToInteger(basePriceValue);
            const discountedBasePriceInt = currencyFloatToInteger(discountedBasePriceValue);
            if (basePriceInt >= 0) {
              baseTotal += itemQuantity * basePriceInt;
            }
            if (discountedBasePriceInt >= 0) {
              discountedBaseTotal += itemQuantity * discountedBasePriceInt;
            }
          };

          if (ctrl.hasPriceList(event)) {
            for (let i = 0, ii = event.priceList.prices.length; i < ii; i++) {
              if (event.priceList.prices[i].quantity >= 0) {
                if (!event.toggleCSRPrices) {
                  if (($filter('saleChannel') as any)([event.priceList.prices[i]], '!customerRep').length) {
                    //this item is hidden, set it's total quantity to 0;
                    event.priceList.prices[i].quantity = 0;
                  }
                }
                quantity += event.priceList.prices[i].quantity;
                if (!ctrl.model.accountId && !event.priceList.prices[i].discountedPrice) {
                  event.priceList.prices[i].discountedPrice = event.priceList.prices[i].price;
                }
                currentPrice = currencyFloatToInteger(ctrl.getPayablePrice(event.priceList.prices[i]));
                if (currentPrice >= 0) {
                  total += event.priceList.prices[i].quantity * currentPrice;
                }
                accumulateBase(event.priceList.prices[i], event.priceList.prices[i].quantity);
              }
            }
          }

          if (event.hasOwnProperty('addOns') && event.addOns.inventoryAddOns instanceof Array) {
            console.log('EventsOrderController::calculateTotal', event.addOns.inventoryAddOns);
            for (let i = 0, ii = event.addOns.inventoryAddOns.length; i < ii; i++) {
              if (event.addOns.inventoryAddOns[i].totalQuantity >= 0) {
                if (!event.toggleCSRAddons) {
                  if (($filter('saleChannel') as any)([event.addOns.inventoryAddOns[i]], '!customerRep').length) {
                    //this item is hidden, set it's total quantity to 0;
                    event.addOns.inventoryAddOns[i].totalQuantity = 0;
                  }
                }
                addOnQuantity += event.addOns.inventoryAddOns[i].totalQuantity;
                currentPrice = currencyFloatToInteger(ctrl.getPayablePrice(event.addOns.inventoryAddOns[i]));
                if (currentPrice >= 0) {
                  total += event.addOns.inventoryAddOns[i].totalQuantity * currentPrice;
                }
                accumulateBase(event.addOns.inventoryAddOns[i], event.addOns.inventoryAddOns[i].totalQuantity);
              }
            }
          }

          event.addOnQuantity = addOnQuantity;
          event.quantity = quantity;
          event.total = integerToCurrencyFloat(total);
          event.baseTotal = integerToCurrencyFloat(baseTotal);
          event.discountedBaseTotal = integerToCurrencyFloat(discountedBaseTotal);
          ctrl.updateReservation();
          ctrl.calculateSubTotal();
        },

        calculateSubTotal: function () {
          let total = 0;
          let baseTotal = 0;
          let discountedBaseTotal = 0;
          let quantity = 0;
          let addOnQuantity = 0;

          for (let i = 0, ii = ctrl.events.length; i < ii; i++) {
            total += currencyFloatToInteger(ctrl.events[i].total);
            baseTotal += currencyFloatToInteger(ctrl.events[i].baseTotal ?? 0);
            discountedBaseTotal += currencyFloatToInteger(ctrl.events[i].discountedBaseTotal ?? 0);
            quantity += ctrl.events[i].quantity;
            addOnQuantity += ctrl.events[i].addOnQuantity;
          }

          ctrl.addOnQuantity = addOnQuantity;
          ctrl.quantity = quantity;
          ctrl.total = integerToCurrencyFloat(total);
          ctrl.subTotal = integerToCurrencyFloat(baseTotal);
          ctrl.discountTotal = integerToCurrencyFloat(baseTotal - discountedBaseTotal);
          ctrl.tax = integerToCurrencyFloat(total - discountedBaseTotal);
        },

        calculatePrice: function (category) {
          const price = ctrl.getDisplayDiscountedPrice(category) * category.quantity;

          return price || 0;
        },

        calculateAddOnPrice: function (addon) {
          const price = ctrl.getDisplayPrice(addon) * addon.totalQuantity;

          return price || 0;
        },

        togglePrices: function (event) {
          event.toggleCSRPrices = !event.toggleCSRPrices;
          // next tick
          $timeout(() => {
            ctrl.calculateTotal(event);
          }, 0);
        },

        /**
         * TODO: b2bAddEventToOrder - should we remove this?
         * @deprecated
         */
        refreshAvailableTicketsQuantity: function (event) {
          const reservedQuantity = event.priceList.prices.reduce((sum, price) => {
            return sum + (price._reservedQuantity || 0);
          }, 0);

          return ApiService.inventoryB2C.detail(event.id).then((eventSummary) => {
            event.soldQuantity = eventSummary.soldQuantity - reservedQuantity;
          });
        },

        toggleAddons: function (event) {
          event.toggleCSRAddons = !event.toggleCSRAddons;
          // next tick
          $timeout(() => {
            ctrl.calculateTotal(event);
          }, 0);
        },

        selectEmailOption: function () {
          if (ctrl.model.noEmail) {
            ctrl.emailOption = '0';
          } else if (ctrl.model.sendEmailToAll) {
            ctrl.emailOption = '2';
          } else if (!ctrl.model.noEmail && !ctrl.model.sendEmailToAll) {
            ctrl.emailOption = '0';
          }
        },

        setEmailOption: function () {
          switch (parseInt(ctrl.emailOption, 10)) {
            case 0:
              ctrl.model.noEmail = true;
              ctrl.model.sendEmailToAll = false;
              break;
            case 1:
              ctrl.model.noEmail = false;
              ctrl.model.sendEmailToAll = false;
              break;
            case 2:
              ctrl.model.noEmail = false;
              ctrl.model.sendEmailToAll = true;
              break;
          }
        },

        migrate: function () {
          let event: TODO;
          let items: TODO;
          let ticketExpireDays;

          ctrl.model.events = [];

          for (let j = 0, jj = ctrl.events.length; j < jj; j++) {
            event = {};
            items = [];
            ticketExpireDays = 0;

            if (ctrl.hasPriceList(ctrl.events[j])) {
              for (let i = 0, ii = ctrl.events[j].priceList.prices.length; i < ii; i++) {
                items.push({
                  itemType: 'Event',
                  personTypeId: ctrl.events[j].priceList.prices[i].personType.id,
                  quantity:
                    ctrl.events[j].priceList.prices[i].quantity >= 0 ? ctrl.events[j].priceList.prices[i].quantity : 0,
                });
                //ctrl.events[j].priceList.prices[i].quantity = 0;
              }
            }

            if (ctrl.events[j].hasOwnProperty('addOns') && ctrl.events[j].addOns.inventoryAddOns instanceof Array) {
              for (let i = 0, ii = ctrl.events[j].addOns.inventoryAddOns.length; i < ii; i++) {
                items.push({
                  itemType: 'Inventory',
                  inventoryId: ctrl.events[j].addOns.inventoryAddOns[i].id,
                  quantity:
                    ctrl.events[j].addOns.inventoryAddOns[i].totalQuantity >= 0
                      ? ctrl.events[j].addOns.inventoryAddOns[i].totalQuantity
                      : 0,
                });
                //ctrl.events[j].addOns.inventoryAddOns[i].totalQuantity = 0;
              }
            }
            if (ctrl.events[j].ticketDetails) {
              if (ctrl.events[j].ticketDetails.ticketDurationDays) {
                ticketExpireDays = ctrl.events[j].ticketDetails.ticketDurationDays;
              }
            }

            event = {
              eventId: ctrl.events[j].id,
              hideTicketEventTime: ctrl.events[j].hideTicketEventTime,
              ticketExpireDays: ticketExpireDays,
              items: items,
            };

            if (ctrl.order.orderRequest) {
              event.orderRequestItemId = ctrl.events[j].orderRequestItemId ? ctrl.events[j].orderRequestItemId : '';
              event.externalReferenceId = ctrl.events[j].externalReferenceId ? ctrl.events[j].externalReferenceId : '';
            }

            ctrl.model.events.push(event);
          }

          ctrl.setEmailOption();
        },

        processPayment: function () {
          const validators = [
            {
              field: 'orders-checkout-remaining',
              equal: {
                message: 'Ticket quantity exceeds available capacity',
                test: function () {
                  let invalid = false;
                  console.log('orders-checkout-remaining', invalid);

                  for (let i = 0, ii = ctrl.events.length; i < ii; i++) {
                    if (!ctrl.isValidQuantity(ctrl.events[i])) {
                      invalid = true;
                      break;
                    }
                  }

                  return [invalid, false];
                },
              },
            },
          ];

          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          ctrl.isSubmitted = true;
          ctrl.errors = {};

          if (ctrl.quantity > 0) {
            if (ValidateService.validate(validators)) {
              if (!ctrl.isPaymentProcessing) {
                ctrl.isPaymentProcessing = true;
                ctrl.validateEmail = parseInt(ctrl.emailOption, 10) === 0 ? false : true;
                $scope.$broadcast('Payment::Process', { validateEmail: ctrl.validateEmail });
                ctrl.processPaymentErrors();
              }
            } else {
              ctrl.errors = ValidateService.errors;
              // TODO: why is this trying to pass an errors object to the notice if we need an array?
              ctrl.notice.showErrorMessages(ctrl.errors as TODO);
              ctrl.isSubmitted = false;
            }
          } else {
            ctrl.notice.showErrorMessages([{ message: 'You must add tickets to the order.' }]);
            ctrl.isSubmitted = false;
          }
        },

        processPaymentErrors: function () {
          const paymentErrors = ValidateService.getErrorHash();

          ctrl.hasEmailError = false;

          angular.forEach(paymentErrors, function (item, index: TODO) {
            if (index === 'payment-email') {
              ctrl.hasEmailError = true;
            }
          });
        },

        updateContact: function (contact) {
          if (FeatureService.isEnabled(FeatureService.customers)) {
            if (!ctrl.hasCustomerId()) {
              ctrl.model.contactFirstName = contact.firstName;
              ctrl.model.contactLastName = contact.lastName;
              ctrl.model.phoneNumber = contact.phoneNumber;
              ctrl.model.email = contact.email;
            }
          }
        },

        clearLoading() {
          ctrl.isSubmitted = false;
          ctrl.isPaymentProcessing = false;
        },

        shouldUseCORSCheckout() {
          return CheckoutProcessService.shouldUseCORSCheckout({ requiresCardData: ctrl.requiresCardData });
        },

        complete: function (payment) {
          payment = payment || {};

          const order: TODO = {
            notes: ctrl.model.notes,
            events: [],
            contactEmail: payment.contactEmail || '',
            contactFirstName: payment.contactFirstName || '',
            contactLastName: payment.contactLastName || '',
            phoneNumber: payment.phoneNumber || '',
            ticketDelivery: payment.ticketDelivery,
            paymentMethod: payment.type,
            invoiceAfterEvent: ctrl.invoiceAfterEvent,
            acmeToken: payment.acmeToken || null,
            creditCardBrand: payment.creditCardBrand || null,
            ccLastFourDigits: payment.ccLastFourDigits || null,
            cvc: payment.cvc || null,
            manualEntryCardNumber: payment.manualEntryCardNumber || null,
            expDate: payment.expDate || null,
            chargeAmount: payment.chargeAmount || null,
            paymentId: payment.paymentId || null,
            saleChannel: 'insideSalesIndividual',
            reservationId: ctrl.reservation && ctrl.reservation.id,
          };

          ctrl.migrate();

          order.noEmail = ctrl.model.noEmail;

          order.events = angular.copy(ctrl.model.events);

          order.address = ctrl.model.address;

          if (ctrl.hasCustomerId()) {
            order.customerId = ctrl.model.customerId;
          }

          if (ctrl.model.accountId) {
            order.accountId = ctrl.model.accountId;
            if (ctrl.selectedAccountContact) {
              order.accountContactId = ctrl.selectedAccountContact.id;
              order.accountContactFirstName = ctrl.selectedAccountContact.firstName || '';
              order.accountContactLastName = ctrl.selectedAccountContact.lastName || '';
              order.accountContactPhoneNumber = ctrl.selectedAccountContact.phoneNumber || '';
              order.accountContactEmail = ctrl.selectedAccountContact.email || '';
            }
            order.saleChannel = 'reseller';
            order.sendEmailToAll = ctrl.model.sendEmailToAll;
          }

          if (ctrl.order.orderRequest) {
            order.orderRequestId = ctrl.order.orderRequest.id ? ctrl.order.orderRequest.id : '';
          }

          /**
           * This is order duplication prevention
           * We create the checkout UUID and tack it onto the checkout request
           * Only rewrite the requestUUID when there are no errors and a checkout has taken place
           * This will always be refreshed when the page is refreshed, but, in the context of an order
           * it should only be overwritten if it has been explicitly set to `false`
           */
          if (!ctrl.requestUUID) {
            ctrl.requestUUID = uuid2.newuuid();
          }

          if (ctrl.shouldUseCORSCheckout()) {
            const data: OrgOrderCreateData = CheckoutProcessService.processOrdersPartnersCreateData(
              {
                ...order,
                requestUUID: ctrl.requestUUID,
              },
              { withCreditCardInfo: false },
            );

            CheckoutProcessService.handleACMEPaymentSessionOrgOrder(ctrl.processACMEPaymentData(data))
              .then((response) => ctrl.completeSuccess({ orderId: response.id }))
              .catch((response) => {
                if (isACMECORSCheckoutError(response)) {
                  switch (response.type) {
                    case 'sdk.render_failed': {
                      ctrl.clearLoading();

                      ctrl.completeError({
                        status: 0,
                        data: { code: 0, errorCode: 0, error: response.params.message },
                      });
                      break;
                    }

                    case 'sdk.cancel_flow': {
                      ctrl.clearLoading();
                      break;
                    }

                    case 'sdk.billing_address.request_change': {
                      ctrl.clearLoading();
                      document.querySelector(`[data-testid="billing-address-anchor"]`)?.scrollIntoView();
                      break;
                    }

                    case 'api.error': {
                      ctrl.completeError(CheckoutProcessService.mapToPaymentProcessError(response));
                      break;
                    }

                    default: {
                      console.warn(`unhandled`, response);

                      ctrl.clearLoading();
                      break;
                    }
                  }
                } else {
                  ctrl.completeError(response);
                }
              });
          } else {
            const data: OrgOrderCreateData = CheckoutProcessService.processOrdersPartnersCreateData(
              {
                ...order,
                requestUUID: ctrl.requestUUID,
              },
              { withCreditCardInfo: true },
            );

            ApiService.orders.partners
              .create(data)
              .then((response) => ctrl.completeSuccess({ orderId: response.data.payload.id }))
              .catch((response) => ctrl.completeError(response));
          }
        },

        processACMEPaymentData: function (data: OrgOrderCreateData): SecureOrgOrderProps {
          const countryData = LocationService.findCountry({ country: data.billingCountry });
          const stateData = LocationService.findState({ state: data.billingState });
          const cssVarsData = CheckoutProcessService.getCORSCheckoutCSSVariables();

          return {
            values: data,
            options: {
              organizationId: ctrl.model.accountId,
              formattedBillingAddress: addressService.formatAddress({
                streetAddress1: data.billingAddress1,
                streetAddress2: data.billingAddress2,
                country: countryData ? countryData.country.name : data.billingCountry,
                zipCode: data.billingZipCode,
                city: data.billingCity,
                state: stateData ? stateData.name : data.billingState,
              }),
              modal: {
                title: 'Payment',
                submit: 'Complete Order',
                close: 'Close',
              },
              authorizePayment: {
                paymentData: {
                  externalId: '',
                  chargeAmount: data.chargeAmount ?? String(ctrl.total),
                  firstName: data.billingFirstName || '',
                  lastName: data.billingLastName || '',
                  creditCardVerificationData: {
                    address1: data.billingAddress1,
                    address2: data.billingAddress2,
                    country: countryData ? String(countryData.country.numericCode) : data.billingCountry,
                    zipCode: data.billingZipCode,
                    city: data.billingCity,
                    state: stateData ? stateData.abbreviation : data.billingState,
                  },
                },
                saleChannel: ctrl.model.accountId ? 'reseller' : 'insideSalesIndividual',
              },
              capturePayment: {
                loaderHtml: CheckoutProcessService.getCheckoutLoadingHtml(),
              },
              autorenew: {
                enabled: false,
                html: ThemesHelperService.getTextVariable('checkout-process-page-autorenew-message'),
                url: '',
              },
              terms: {
                html: ThemesHelperService.getHTMLVariable('checkout-process-page-terms'),
                url: '',
              },
              creditCardSettings: {
                supportedCardsText: ThemesHelperService.getTextVariable('checkout-supported-cards-text'),
                showVisaLogo: ThemesHelperService.getTextVariable('checkout-show-visa-logo'),
                showMcLogo: ThemesHelperService.getTextVariable('checkout-show-mc-logo'),
                showDiscoverLogo: ThemesHelperService.getTextVariable('checkout-show-discover-logo'),
                showAmexLogo: ThemesHelperService.getTextVariable('checkout-show-amex-logo'),
              },
              variables: cssVarsData,
            },
          };
        },

        cancel: function () {
          if ($state.params.accountId && ctrl.order && ctrl.order.cart) {
            OrderService.clear();
            $state.go('application.programs.list');
          } else if ($state.params.accountId && ctrl.order && ctrl.order.orderRequest) {
            window.history.go(-1);
          } else if ($state.params.accountId && ctrl.order) {
            // TODO: APP-4969 - should we deprecate/remove this code path?
            $state.go('application.orders.create', {
              accountId: $state.params.accountId,
              eventTemplateId: ctrl.event.templateId,
            });
          } else if ($state.params.accountId && !ctrl.order) {
            window.history.go(-1);
          } else {
            $state.go('application.programs.list');
          }
        },

        showEmailErrorMessage: function (emailOption) {
          return ctrl.hasEmailError && parseInt(ctrl.emailOption, 10) === parseInt(emailOption, 10);
        },

        onAddressChanged: function ($event) {
          ctrl.model.address = $event;
        },

        onPaymentComplete: function (e, data) {
          CheckoutProcessService.setPaymentData(data);
          ctrl.complete(data);
        },

        onPaymentError: function (e, data) {
          ctrl.isSubmitted = false;
          ctrl.isPaymentProcessing = false;

          ctrl.notice.showErrorMessages([{ message: data }]);

          $scope.noticeTimer = $timeout(() => {
            ctrl.notice.hideSuccessMessages();
          }, 3000);
        },

        onSelectedAccountUpdate: function (n, o) {
          if (n) {
            ctrl.updateContact(n);
          }
        },

        onCheckedInvoiceAfterEvent: function (n, o) {
          if (n) {
            ctrl.emailOption = '0';
          }
        },

        // utils
        getMembershipIdFromOrder,
      };

      $scope.ordersCheckout = ctrl;
      ctrl.init();
    },
  ]).name;
